"use client";

import { useEffect, type RefObject } from "react";

interface Options {
  /** When false, no listeners are attached. Defaults to true. */
  enabled?: boolean;
  /** Also close on Escape keydown. Defaults to false. */
  escape?: boolean;
}

/**
 * Close-on-outside-click (and optionally Escape) for popovers, dropdowns,
 * and tooltips. Attaches listeners only while `enabled` is true and
 * detaches on cleanup.
 */
export function useOutsideClick<T extends HTMLElement>(
  ref: RefObject<T | null>,
  onOutside: () => void,
  options: Options = {},
): void {
  const { enabled = true, escape = false } = options;

  useEffect(() => {
    if (!enabled) return;

    const handleMouseDown = (e: MouseEvent) => {
      const node = ref.current;
      if (node && !node.contains(e.target as Node)) {
        onOutside();
      }
    };
    document.addEventListener("mousedown", handleMouseDown);

    let handleKey: ((e: KeyboardEvent) => void) | null = null;
    if (escape) {
      handleKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") onOutside();
      };
      document.addEventListener("keydown", handleKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      if (handleKey) document.removeEventListener("keydown", handleKey);
    };
  }, [enabled, escape, ref, onOutside]);
}
